<script setup lang="ts">
import { type Locale, formatDate, negateMoney, type Money } from '@taler/domain'
import { signTone } from '@/app/accounting-view'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ChartPeriod } from '../internal/cash-flow-series.ts'

defineProps<{
  periods: readonly ChartPeriod[]
  totals: { income: Money; expense: Money; net: Money }
  caption: string
}>()
const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as Locale)
</script>

<template>
  <div
    class="overflow-x-auto rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)]"
  >
    <table class="w-full border-collapse text-left text-[length:var(--text-step-2)]">
      <caption class="sr-only">
        {{
          caption
        }}
      </caption>
      <thead class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
        <tr>
          <th scope="col" class="px-[var(--space-group)] py-[var(--space-inline)] font-medium">
            {{ t('reporting.table.period') }}
          </th>
          <th scope="col" class="px-[var(--space-group)] py-[var(--space-inline)] text-right font-medium">
            {{ t('reporting.chart.income') }}
          </th>
          <th scope="col" class="px-[var(--space-group)] py-[var(--space-inline)] text-right font-medium">
            {{ t('reporting.chart.expenses') }}
          </th>
          <th scope="col" class="px-[var(--space-group)] py-[var(--space-inline)] text-right font-medium">
            {{ t('reporting.chart.net') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="period in periods"
          :key="period.start.epochDay"
          class="border-t border-[color:var(--color-border)]"
        >
          <th
            scope="row"
            class="px-[var(--space-group)] py-[var(--space-inline)] font-medium text-[color:var(--color-text)]"
          >
            {{ formatDate(period.start, currentLocale, 'short') }}
          </th>
          <td class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            <MoneyAmount :value="period.income" sign tone="income" />
          </td>
          <td class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            <MoneyAmount :value="negateMoney(period.expense)" sign tone="expense" />
          </td>
          <td class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            <MoneyAmount :value="period.net" sign :tone="signTone(period.net)" />
          </td>
        </tr>
      </tbody>
      <tfoot class="border-t-2 border-[color:var(--color-border-control)] font-semibold">
        <tr>
          <th
            scope="row"
            class="px-[var(--space-group)] py-[var(--space-inline)] text-[color:var(--color-text)]"
          >
            {{ t('reporting.total') }}
          </th>
          <td class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            <MoneyAmount :value="totals.income" sign tone="income" />
          </td>
          <td class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            <MoneyAmount :value="negateMoney(totals.expense)" sign tone="expense" />
          </td>
          <td class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            <MoneyAmount :value="totals.net" sign :tone="signTone(totals.net)" />
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>
